export const AttendanceStatus = {
  ABSENT: 1, // 未出席
  PRESENT: 2, // 出席
  LATE: 3, // 遲到
  EARLY_LEAVE: 4, // 早退
  LEAVE: 5, // 請假
  OVERTIME: 6, // 加班
  LATE_IN: 7, // 未打上班卡
  LATE_OUT: 8, // 未打下班卡
  ABNORMAL: 10, // 異常
  // 請假期間打卡
  LEAVE_CLOCK_IN: 11,
};

export const StatusDisplayText = {
  [AttendanceStatus.ABSENT]: "缺席",
  [AttendanceStatus.PRESENT]: "出席",
  [AttendanceStatus.LATE]: "遲到",
  [AttendanceStatus.EARLY_LEAVE]: "早退",
  [AttendanceStatus.LEAVE]: "請假",
  [AttendanceStatus.OVERTIME]: "加班",
  [AttendanceStatus.LATE_IN]: "未打上班卡",
  [AttendanceStatus.LATE_OUT]: "未打下班卡",
  [AttendanceStatus.ABNORMAL]: "異常",
  [AttendanceStatus.LEAVE_CLOCK_IN]: "請假期間打卡",
};

/**
 * Get the color for a status
 * @param {Number} status
 * @returns {String}
 */
export function getStatusColor(status) {
  switch (status) {
    case AttendanceStatus.ABSENT:
      return "red";
    case AttendanceStatus.PRESENT:
      return "green";
    case AttendanceStatus.LATE:
    case AttendanceStatus.EARLY_LEAVE:
      return "amber darken-2";
    case AttendanceStatus.LEAVE:
      return "blue";
    case AttendanceStatus.OVERTIME:
      return "yellow darken-3";
    case AttendanceStatus.LATE_IN:
      return "purple lighten-1";
    case AttendanceStatus.LATE_OUT:
      return "purple darken-1";
    case AttendanceStatus.ABNORMAL:
      return "deep-orange";
    case AttendanceStatus.LEAVE_CLOCK_IN:
      return "red";
    default:
      return "grey";
  }
}

/**
 * Get the display text for a status
 * @param {Number} status
 * @returns {String}
 */
export function getStatusText(status) {
  return StatusDisplayText[status] || "未知";
}

/**
 * Calculate working hours for a record
 * @param {Object} record
 * @returns {Number} hours
 */
export function calculateWorkingHours(record) {
  if (!record || !record.clock_in_actual || !record.clock_out_actual) {
    return 0;
  }

  const clockIn = new Date(record.clock_in_actual);
  const clockOut = new Date(record.clock_out_actual);
  let durationMs = clockOut - clockIn;

  // Validate dates
  if (isNaN(durationMs) || durationMs < 0) return 0;

  if (record.employeeClockSetting && record.employeeClockSetting.lunch_break) {
    const setting = record.employeeClockSetting;
    if (setting.lunch_break_start && setting.lunch_break_end) {
      // Create date objects for lunch start/end based on clockIn date
      // We assume lunch break occurs on the same day as clock-in for this calculation context,
      // or simply align the time portion if the shifts are standard day shifts.
      // Use setHours from the setting string "HH:mm:ss"

      const parseTime = (dateBase, timeStr) => {
        const d = new Date(dateBase);
        const [h, m, s] = timeStr.split(":").map(Number);
        d.setHours(h, m, s || 0, 0);
        return d;
      };

      const lunchStart = parseTime(clockIn, setting.lunch_break_start);
      const lunchEnd = parseTime(clockIn, setting.lunch_break_end);

      // Handle lunch break spanning midnight if necessary, but standard assumes day shift.
      // If lunchEnd < lunchStart, it might mean next day, but usually settings are "12:00" to "13:00".

      // Calculate overlap
      const overlapStart = new Date(Math.max(clockIn, lunchStart));
      const overlapEnd = new Date(Math.min(clockOut, lunchEnd));

      if (overlapStart < overlapEnd) {
        const deduction = overlapEnd - overlapStart;
        durationMs -= deduction;
      }
    }
  }

  const hours = durationMs / (1000 * 60 * 60);
  return Math.max(0, parseFloat(hours.toFixed(1)));
}

function getWeekdayIndex(dayStr) {
  if (!dayStr) return -1;
  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  return days.indexOf(dayStr);
}

/**
 * Calculate scheduled working minutes between two datetimes based on clock settings
 * @param {Date} startCal
 * @param {Date} endCal
 * @param {Object} clockSetting
 * @param {Number} applyType - The type of application (1: Leave, 2: Overtime, etc.)
 * @returns {Number} minutes
 */
export function calculateScheduledWorkingMinutes(
  startCal,
  endCal,
  clockSetting,
  applyType
) {
  // Basic validation
  if (startCal >= endCal || !clockSetting) return 0;

  const baseMinutes = Math.floor((endCal - startCal) / 60000);
  if (!clockSetting.fixed_working_days) {
    return baseMinutes;
  }

  const parseTime = (timeStr) => {
    if (!timeStr) return null;
    const [h, m] = timeStr.split(":").map(Number);
    return h * 60 + m;
  };

  const clockIn = parseTime(clockSetting.clock_in_time);
  const clockOut = parseTime(clockSetting.clock_out_time);

  if (clockIn === null || clockOut === null) {
    return baseMinutes;
  }

  // Handle lunch break
  const hasLunchBreak = clockSetting.lunch_break;
  const lunchStart = parseTime(clockSetting.lunch_break_start);
  const lunchEnd = parseTime(clockSetting.lunch_break_end);

  const recessWeekday = getWeekdayIndex(clockSetting.recess_day);
  const regularWeekday = getWeekdayIndex(clockSetting.regular_day);

  const isNightShift = clockOut <= clockIn;

  const startOfDay = (d) => {
    const n = new Date(d);
    n.setHours(0, 0, 0, 0);
    return n;
  };

  const dateBySettingHM = (dayDate, totalMinutes) => {
    const n = new Date(dayDate);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    n.setHours(h, m, 0, 0);
    return n;
  };

  const addDays = (d, days) => {
    const n = new Date(d);
    n.setDate(n.getDate() + days);
    return n;
  };

  const overlapMinutes = (aStart, aEnd, bStart, bEnd) => {
    const s = aStart > bStart ? aStart : bStart;
    const e = aEnd < bEnd ? aEnd : bEnd;
    if (s >= e) return 0;
    return Math.floor((e - s) / 60000);
  };

  const subtractInterval = (segs, bs, be) => {
    const result = [];
    for (const [s, e] of segs) {
      if (bs >= e || be <= s) {
        result.push([s, e]);
      } else if (bs <= s && be >= e) {
        // Completely covered
      } else if (bs > s && be < e) {
        result.push([s, bs]);
        result.push([be, e]);
      } else if (bs <= s) {
        result.push([be, e]);
      } else {
        result.push([s, bs]);
      }
    }
    return result;
  };

  const segmentsForDay = (day) => {
    const dayStart = startOfDay(day);
    const nextDay = addDays(dayStart, 1);

    const s1 = dateBySettingHM(dayStart, clockIn);
    const e1 = isNightShift
      ? dateBySettingHM(nextDay, clockOut)
      : dateBySettingHM(dayStart, clockOut);

    let segs = [[s1, e1]];

    const getBreakInterval = (breakStart, breakEnd) => {
      let rs = dateBySettingHM(dayStart, breakStart);
      let re = dateBySettingHM(dayStart, breakEnd);
      if (isNightShift) {
        if (breakStart < clockIn) {
          rs = dateBySettingHM(nextDay, breakStart);
        }
        if (breakEnd < clockIn) {
          re = dateBySettingHM(nextDay, breakEnd);
        }
      }
      return [rs, re];
    };

    // Lunch break
    const hasValidRest =
      hasLunchBreak &&
      lunchStart !== null &&
      lunchEnd !== null &&
      lunchEnd > lunchStart;

    if (hasValidRest) {
      const [rs, re] = getBreakInterval(lunchStart, lunchEnd);
      segs = subtractInterval(segs, rs, re);
    }

    // Rest 1 break
    const rest1Start = parseTime(clockSetting.rest_1_start);
    const rest1End = parseTime(clockSetting.rest_1_end);
    const hasValidRest1 =
      (clockSetting.rest_1_start_enable === true ||
        clockSetting.rest_1_start_enable === "true") &&
      rest1Start !== null &&
      rest1End !== null &&
      rest1End > rest1Start;

    if (hasValidRest1) {
      const [rs, re] = getBreakInterval(rest1Start, rest1End);
      segs = subtractInterval(segs, rs, re);
    }

    return segs.filter((pair) => pair[1] > pair[0]);
  };

  const weekendSet = new Set();
  if (recessWeekday !== -1) weekendSet.add(recessWeekday);
  if (regularWeekday !== -1) weekendSet.add(regularWeekday);

  let dayCursor = startOfDay(startCal);
  const endDay = startOfDay(endCal);
  let total = 0;

  while (dayCursor <= endDay) {
    const weekday = dayCursor.getDay(); // 0=Sunday
    if (!weekendSet.has(weekday)) {
      const segs = segmentsForDay(dayCursor);
      for (const [s, e] of segs) {
        total += overlapMinutes(startCal, endCal, s, e);
      }
    }
    dayCursor = addDays(dayCursor, 1);
  }

  // If it's Overtime Application (type 2), we want the duration OUTSIDE scheduled working hours
  if (applyType === 2) {
    return Math.max(0, baseMinutes - total);
  }

  return Math.max(0, total);
}

/**
 * Format leave duration text based on request data and clock settings
 * @param {Object} request - Request object containing start_time, end_time, apply_type, leave_minutes, applicant
 * @param {String} unit - Display unit: 'hours' or 'days'
 * @returns {String} formatted text
 */
export function formatLeaveDurationText(request, unit = "hours") {
  let dayMinutes = 480; // Default 8 hours
  const clockSetting = request.applicant?.employeeClockSetting;

  if (clockSetting && request.start_time && request.end_time) {
    if (clockSetting.clock_in_time && clockSetting.clock_out_time) {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      const dummyStart = new Date(d);
      const dummyEnd = new Date(d);
      dummyEnd.setHours(23, 59, 59, 999);

      const singleDayMins = calculateScheduledWorkingMinutes(
        dummyStart,
        dummyEnd,
        {
          ...clockSetting,
          fixed_working_days: true,
          recess_day: "",
          regular_day: "",
        }
      );

      if (singleDayMins > 0) {
        dayMinutes = singleDayMins;
      }
    }
  }

  const formatValue = (value) => {
    if (Math.abs(value - Math.round(value)) < 1e-9) {
      return String(Math.round(value));
    }
    return value.toFixed(1);
  };

  const isUnpaidLeave =
    request.apply_type === 10 || request.apply_type === "10";

  if (unit === "hours") {
    if (isUnpaidLeave) {
      if (request.start_time && request.end_time) {
        const start = new Date(request.start_time);
        start.setHours(0, 0, 0, 0);
        const end = new Date(request.end_time);
        end.setHours(0, 0, 0, 0);
        const days = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
        const totalHours = days * (dayMinutes / 60);
        return `${formatValue(totalHours)}小時`;
      }
      return "0小時";
    }

    let totalMinutes = Number(request.leave_minutes || 0);
    if (clockSetting && request.start_time && request.end_time) {
      const startCal = new Date(request.start_time);
      const endCal = new Date(request.end_time);

      const calculated = calculateScheduledWorkingMinutes(
        startCal,
        endCal,
        clockSetting,
        request.apply_type
      );
      if (calculated > 0) {
        totalMinutes = calculated;
      }
    }

    if (!Number.isFinite(totalMinutes)) {
      return "0小時";
    }

    const totalHours = totalMinutes / 60;
    return `${formatValue(totalHours)}小時`;
  }

  // Legacy days + hours format
  if (isUnpaidLeave) {
    if (request.start_time && request.end_time) {
      const start = new Date(request.start_time);
      start.setHours(0, 0, 0, 0);
      const end = new Date(request.end_time);
      end.setHours(0, 0, 0, 0);
      const days = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
      return `${days}天`;
    }
    return "0天";
  }

  let totalMinutes = Number(request.leave_minutes || 0);
  if (clockSetting && request.start_time && request.end_time) {
    const startCal = new Date(request.start_time);
    const endCal = new Date(request.end_time);

    const calculated = calculateScheduledWorkingMinutes(
      startCal,
      endCal,
      clockSetting,
      request.apply_type
    );
    if (calculated > 0) {
      totalMinutes = calculated;
    }
  }

  if (!Number.isFinite(totalMinutes)) {
    return "0天0小時";
  }

  const totalHours = totalMinutes / 60;
  const dayHours = dayMinutes / 60;

  const days = Math.floor(totalHours / dayHours);
  const remainingHours = totalHours - days * dayHours;

  return `${days}天${formatValue(Math.max(remainingHours, 0))}小時`;
}
